import { useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';

import { OrganisationWithCounts } from '../../../../../../backend/src/types/types';
import { Breadcrumb } from '../../../components/breadcrumb';
import { TableFooter } from '../../../components/table-footer';
import { useGetAllOrganisations } from '@/api/queries/useGetOrganisations';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/new/components/shadcn/table';
import { CellWithCopy } from '@/new/components/table-cell-with-copy';

const fallbackTableData: OrganisationWithCounts[] = [];
const columnHelper = createColumnHelper<OrganisationWithCounts>();

const columns = [
  columnHelper.accessor('name', {
    header: () => <span>Organisation Name</span>,
    cell: (info) => <span className="line-clamp-1 min-w-48 break-all">{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: 'manageOrgs',
    header: () => <span>Manage Organisation</span>,
    cell: (props) => (
      <Link to={`/admin/organisations/${props.row.original.id}`}>
        <Button size="sm">Manage Organisation</Button>
      </Link>
    ),
  }),
  columnHelper.accessor('id', {
    header: () => <span>Organisation Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-24 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('userCount', {
    header: () => <span>No. of Users</span>,
    cell: (info) => info.getValue(),
  }),
  columnHelper.display({
    id: 'manageUsers',
    header: () => <span>Manage Users</span>,
    cell: (props) => (
      <Link to={`/admin/organisations/${props.row.original.id}/users`}>
        <Button size="sm">Manage Users</Button>
      </Link>
    ),
  }),
  columnHelper.accessor('matterCount', {
    header: () => <span>No. of Matters</span>,
    cell: (info) => info.getValue(),
  }),
  columnHelper.display({
    id: 'manageRuns',
    header: () => <span>Runs</span>,
    cell: (props) => (
      <Link to={`/admin/organisations/${props.row.original.id}/runs`}>
        <Button size="sm">Manage Runs</Button>
      </Link>
    ),
  }),
];

export function Admin() {
  useGlobalAdminCheck();

  const { data: organisations, isLoading } = useGetAllOrganisations();

  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: organisations ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'userCount',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb items={[{ href: '/admin/organisations', label: 'Admin', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Admin" />
        </PageHeader>

        <SearchInput
          placeholder="Search organisations..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="my-6 max-w-sm"
        />

        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination Controls */}
        {!isLoading && <TableFooter table={table} plural="organisations" />}
      </PageContainer>
    </>
  );
}
